import React, { useState, useEffect, useCallback } from 'react'
import Sidebar, { NAV_ITEMS, NavIcon } from '../components/Sidebar'
import { useLanguage, useDateLocale } from '../LanguageContext'
import PrivateAviation  from './PrivateAviation'
import PartnerHotels    from './PartnerHotels'
import CarTransfers     from './CarTransfers'
import AirportServices  from './AirportServices'
import MemberProfile    from './MemberProfile'
import AboutUs          from './AboutUs'
import Blog             from './Blog'
import RequestHistory   from './RequestHistory'
import { getHistory, getProfile } from '../api'

const VIDEO_WEBM = '/images/video1.mp4'
const VIDEO_MP4  = '/images/video1.mp4'

const STORAGE_BASE = 'https://api.mwuilu.com/public/'

function buildAvatarUrl(path) {
  if (!path) return null
  if (path.startsWith('http') || path.startsWith('blob:')) return path
  return STORAGE_BASE + path.replace(/^\//, '')
}

/** Derive up to 2 initials from a full name, skipping honorifics */
function deriveInitials(fullName) {
  if (!fullName) return ''
  const skip = new Set(['H.E.', 'H.R.H.', 'Dr.', 'Prof.', 'Mr.', 'Ms.', 'Mrs.', 'Sir', 'Lord', 'Ambassador'])
  const words = fullName.split(' ').filter(w => w && !skip.has(w))
  if (words.length === 0) return fullName.slice(0, 2).toUpperCase()
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

const getGreeting = (t) => {
  const h = new Date().getHours()
  if (h < 12) return t('goodMorning')
  if (h < 18) return t('goodAfternoon')
  return t('goodEvening')
}

const getFirstName = (fullName) => {
  const skip = ['H.E.', 'H.R.H.', 'Dr.', 'Prof.', 'Mr.', 'Ms.', 'Mrs.', 'Sir', 'Lord', 'Ambassador']
  const parts = fullName.split(' ')
  return parts.filter(p => !skip.includes(p))[0] || parts[0]
}

const SERVICE_I18N_KEY = {
  private_aviation: 'servicePrivateAviation',
  partner_hotels:   'servicePartnerHotels',
  car_transfers:    'serviceCarTransfers',
  airport_services: 'serviceAirportServices',
}

function buildDetail(service, payload) {
  if (!payload) return '—'
  switch (service) {
    case 'private_aviation': {
      const from = payload.origin?.code      || payload.origin?.city      || '?'
      const to   = payload.destination?.code || payload.destination?.city || '?'
      return `${from} → ${to}`
    }
    case 'partner_hotels': {
      const city    = payload.city    || payload.country || '?'
      const checkIn = payload.checkIn || ''
      return checkIn ? `${city} — ${checkIn}` : city
    }
    case 'car_transfers': {
      const from = payload.pickup  || '?'
      const to   = payload.dropoff || '?'
      return `${from} → ${to}`
    }
    case 'airport_services': {
      const airport = payload.airport || '?'
      const flight  = payload.flightNumber || ''
      return flight ? `${airport} · ${flight}` : airport
    }
    default:
      return '—'
  }
}

function useFormatDate() {
  const locale = useDateLocale()
  return useCallback((isoString) => {
    if (!isoString) return '—'
    return new Date(isoString).toLocaleDateString(locale, {
      day:   '2-digit',
      month: 'short',
      year:  'numeric',
    })
  }, [locale])
}

const MOCK_COMPANIONS = [
  { name: 'Marie Al-Rashidi',  initials: 'MA', role: 'Spouse',    passport: 'AE-123456', nationality: 'United Arab Emirates' },
  { name: 'Fatima Al-Rashidi', initials: 'FA', role: 'Assistant', passport: 'AE-654321', nationality: 'United Arab Emirates' },
]

export default function Dashboard({ onSignOut, user }) {
  const { t } = useLanguage()
  const formatDate = useFormatDate()

  const [activeSection, setActiveSection] = useState('home')
  const [collapsed,     setCollapsed]     = useState(false)
  const [isMobile,      setIsMobile]      = useState(false)
  const [drawerOpen,    setDrawerOpen]    = useState(false)
  const [entered,       setEntered]       = useState(false)

  // ── Live member state (lifted from MemberProfile) ──────────────────────
  // Populated by fetching /profile on mount so sidebar always shows
  // the real name, initials and avatar — not the stale `user` prop.
  const [memberName,     setMemberName]     = useState(user?.name      || '')
  const [memberMemberId, setMemberMemberId] = useState(user?.member_id || '')
  const [memberInitials, setMemberInitials] = useState(deriveInitials(user?.name || ''))
  const [memberAvatarUrl,setMemberAvatarUrl]= useState(null)   // full URL or null
  const [profileLoaded,  setProfileLoaded]  = useState(false)

  // Fetch full profile once on mount so sidebar is correct immediately
  useEffect(() => {
    getProfile()
      .then(({ profile }) => {
        if (!profile) return
        setMemberName(profile.name || '')
        setMemberMemberId(profile.member_id || '')
        setMemberInitials(
          profile.initials
            ? profile.initials.toUpperCase()
            : deriveInitials(profile.name || '')
        )
        setMemberAvatarUrl(buildAvatarUrl(profile.avatar_url || null))
        setProfileLoaded(true)
      })
      .catch(() => setProfileLoaded(true))   // fail silently — sidebar still shows name from `user`
  }, [])

  // ── Request history ────────────────────────────────────────────────────
  const [requests,        setRequests]        = useState([])
  const [requestsLoading, setRequestsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => setEntered(true), 60)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const fetchRequests = () => {
    setRequestsLoading(true)
    getHistory()
      .then(({ requests }) => setRequests(requests || []))
      .catch(() => setRequests([]))
      .finally(() => setRequestsLoading(false))
  }

  useEffect(() => { fetchRequests() }, [])
  useEffect(() => { if (activeSection === 'home') fetchRequests() }, [activeSection])

  const navigate = (id) => {
    setActiveSection(id)
    if (isMobile) setDrawerOpen(false)
  }

  // ── Member object for Sidebar (always up-to-date) ─────────────────────
  const member = {
    name:      memberName      || user?.name      || '',
    memberId:  memberMemberId  || user?.member_id || '',
    initials:  memberInitials,
    avatarUrl: memberAvatarUrl,   // full URL string or null → Sidebar handles both
  }

  // ── Avatar update callback (called by MemberProfile on upload) ─────────
  // The profile page calls this immediately with the blob URL for instant
  // feedback, then again with the permanent URL once the upload finishes.
  const handleAvatarUpdate = useCallback((fullUrl) => {
    setMemberAvatarUrl(fullUrl)
  }, [])

  const recentRequests = requests.slice(0, 3)

  return (
    <div className={`db-shell${entered ? ' db-shell--in' : ''}`}>

      <div className="db-bg" aria-hidden="true">
        <video className="db-bg__video" autoPlay muted loop playsInline preload="auto">
          <source src={VIDEO_WEBM} type="video/webm" />
          <source src={VIDEO_MP4}  type="video/mp4"  />
        </video>
        <div className="db-bg__overlay" />
        <div className="db-bg__grain"   />
      </div>

      {isMobile && drawerOpen && (
        <div className="db-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      )}

      <Sidebar
        activeSection={activeSection}
        collapsed={collapsed}
        isMobile={isMobile}
        drawerOpen={drawerOpen}
        onNavigate={navigate}
        onCollapse={() => setCollapsed(c => !c)}
        onSignOut={onSignOut}
        member={member}
      />

      <div className="db-main">

        {isMobile && (
          <div className="db-topbar">
            <button className="db-topbar__burger" onClick={() => setDrawerOpen(d => !d)} aria-label={t('openMenu')}>
              <span /><span /><span />
            </button>
            <img src="/images/logo.png" alt={t('logoAlt')} />
            <div style={{ width: 36 }} />
          </div>
        )}

        {/* ── HOME ── */}
        {activeSection === 'home' && (
          <div className="db-page db-home">

            <header className="db-home__greeting">
              <div className="db-home__greeting-left">
                <p className="db-home__salutation">
                  {getGreeting(t)},&nbsp;<em>{getFirstName(member.name)}</em>
                </p>
                <h1 className="db-home__headline">
                  {t('howMayWeAttendLine1')}<br />{t('howMayWeAttendLine2')}
                </h1>
              </div>
              <div className="db-home__greeting-right">
                <p className="db-urgent-link">
                  <a
                    href="https://wa.me/3246569942"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cnt-about-contact__link cnt-about-contact__link--icon"
                    aria-label="WhatsApp +32 465 69 94 23"
                  >
                    <span className="cnt-about-contact__icon-wrap" style={{ marginRight: '8px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </span>
                  </a>
                </p>
              </div>
            </header>

            <section aria-label={t('servicesRegion')}>
              <p className="db-overline">{t('ourServices')}</p>
              <div className="db-tiles">
                <ServiceTile icon={<IconAviation />}  label={t('tileFlightTitle')}    desc={t('tileFlightDesc')}    onClick={() => navigate('aviation')}  />
                <ServiceTile icon={<IconHotels />}    label={t('tileHotelsTitle')}    desc={t('tileHotelsDesc')}    onClick={() => navigate('hotels')}    />
                <ServiceTile icon={<IconTransfers />} label={t('tileTransfersTitle')} desc={t('tileTransfersDesc')} onClick={() => navigate('transfers')} />
                <ServiceTile icon={<IconAirport />}   label={t('tileAirportTitle')}   desc={t('tileAirportDesc')}   onClick={() => navigate('airport')}   />
              </div>
            </section>

            <section aria-label={t('recentRequestsRegion')}>
              <div className="db-section-header-row">
                <p className="db-overline">{t('recentRequests')}</p>
                {requests.length > 0 && (
                  <button className="db-view-all" onClick={() => navigate('history')}>
                    {t('viewAll')}
                  </button>
                )}
              </div>

              {requestsLoading ? (
                <div className="db-requests-loading">
                  <span className="db-requests-loading__dot" />
                  <span className="db-requests-loading__dot" />
                  <span className="db-requests-loading__dot" />
                </div>
              ) : recentRequests.length === 0 ? (
                <p className="db-requests-empty">{t('noRequestsYet')}</p>
              ) : (
                <div className="db-requests">
                  {recentRequests.map((req, i) => (
                    <div
                      key={req.id}
                      className="db-request"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <div className="db-request__left">
                        <span className="db-request__service">
                          {SERVICE_I18N_KEY[req.service] ? t(SERVICE_I18N_KEY[req.service]) : req.service}
                        </span>
                        <span className="db-request__detail">
                          {buildDetail(req.service, req.payload)}
                        </span>
                      </div>
                      <div className="db-request__right">
                        <span className="db-request__date">
                          {formatDate(req.submitted_at)}
                        </span>
                        <StatusBadge statusKey={req.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

          </div>
        )}

        {/* ── PRIVATE AVIATION ── */}
        {activeSection === 'aviation' && (
          <PrivateAviation
            onBack={() => navigate('home')}
            member={member}
            savedCompanions={MOCK_COMPANIONS}
          />
        )}

        {/* ── REQUEST HISTORY ── */}
        {activeSection === 'history' && (
          <RequestHistory
            onBack={() => navigate('home')}
            requests={requests}
            loading={requestsLoading}
          />
        )}

        {/* ── PARTNER HOTELS ── */}
        {activeSection === 'hotels' && (
          <PartnerHotels
            onBack={() => navigate('home')}
            onNavigate={navigate}
            member={member}
          />
        )}

        {/* ── CAR TRANSFERS ── */}
        {activeSection === 'transfers' && (
          <CarTransfers
            onBack={() => navigate('home')}
            onNavigate={navigate}
            member={member}
          />
        )}

        {/* ── AIRPORT SERVICES ── */}
        {activeSection === 'airport' && (
          <AirportServices
            onBack={() => navigate('home')}
            onNavigate={navigate}
            member={member}
          />
        )}

        {/* ── MEMBER PROFILE ── onAvatarUpdate wired here ── */}
        {activeSection === 'profile' && (
          <MemberProfile
            onBack={() => navigate('home')}
            onAvatarUpdate={handleAvatarUpdate}
          />
        )}

        {/* ── ABOUT ── */}
        {activeSection === 'about' && (
          <AboutUs
            onBack={() => navigate('home')}
            onNavigate={navigate}
            member={member}
          />
        )}

        {/* ── BLOG ── */}
        {activeSection === 'blog' && (
          <Blog
            onBack={() => navigate('home')}
            onNavigate={navigate}
            member={member}
          />
        )}

      </div>

      {isMobile && (
        <nav className="db-bottom-nav" aria-label={t('bottomNavigation')}>
          <button
            className={`db-bottom-nav__item${activeSection === 'home' ? ' db-bottom-nav__item--active' : ''}`}
            onClick={() => navigate('home')}
          >
            <IconHomeSmall />
            <span>{t('home')}</span>
          </button>
          {NAV_ITEMS.slice(0, 3).map(item => (
            <button
              key={item.id}
              className={`db-bottom-nav__item${activeSection === item.id ? ' db-bottom-nav__item--active' : ''}`}
              onClick={() => navigate(item.id)}
            >
              <NavIcon id={item.id} />
              <span>{
                item.id === 'aviation'  ? t('navShortAviation')
                  : item.id === 'hotels' ? t('navShortHotels')
                  : t('navShortTransfers')
              }</span>
            </button>
          ))}
          <button className="db-bottom-nav__item" onClick={() => setDrawerOpen(true)}>
            <IconMore />
            <span>{t('more')}</span>
          </button>
        </nav>
      )}

    </div>
  )
}

function ServiceTile({ icon, label, desc, onClick }) {
  return (
    <button className="db-tile" onClick={onClick}>
      <div className="db-tile__icon">{icon}</div>
      <span className="db-tile__label">{label}</span>
      <span className="db-tile__desc">{desc}</span>
      <span className="db-tile__caret" aria-hidden="true">→</span>
    </button>
  )
}

function StatusBadge({ statusKey }) {
  const { t } = useLanguage()
  const labelKey = {
    submitted:   'status_submitted',
    confirmed:   'status_confirmed',
    in_progress: 'status_in_progress',
    completed:   'status_completed',
    cancelled:   'status_cancelled',
  }[statusKey] || 'status_submitted'
  const cls = {
    submitted:   'db-badge--received',
    confirmed:   'db-badge--confirmed',
    in_progress: 'db-badge--progress',
    completed:   'db-badge--completed',
    cancelled:   'db-badge--cancelled',
  }
  return <span className={`db-badge ${cls[statusKey] ?? ''}`}>{t(labelKey)}</span>
}

const SV = { fill: 'none', stroke: 'currentColor', strokeWidth: '0.9', strokeLinecap: 'round', strokeLinejoin: 'round' }
function IconHomeSmall() { return <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 7L8 2L14 7V14H10V10H6V14H2V7Z" {...SV} /></svg> }
function IconMore()      { return <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="1" fill="currentColor" /><circle cx="3" cy="8" r="1" fill="currentColor" /><circle cx="13" cy="8" r="1" fill="currentColor" /></svg> }
function IconAviation()  { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M22 7.5L16 13H22L18.5 20.5H15.5L13 14.5L3.5 19V16.5L11 12.5L3.5 5V2.5L13 6.5L15.5 1H18.5L22 7.5Z" {...SV} /></svg> }
function IconHotels()    { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><rect x="3" y="10" width="20" height="13" rx="0.5" {...SV} /><path d="M7.5 23V18H10.5V23" {...SV} /><rect x="14" y="17" width="5" height="4" {...SV} /><path d="M3 10L13 3L23 10" {...SV} /></svg> }
function IconTransfers() { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><rect x="2" y="10" width="22" height="8" rx="2" {...SV} /><path d="M5 10L7.5 5H18.5L21 10" {...SV} /><circle cx="7" cy="20" r="2" {...SV} /><circle cx="19" cy="20" r="2" {...SV} /></svg> }
function IconAirport()   { return <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M4 22H22" {...SV} /><path d="M13 5V17" {...SV} /><path d="M8.5 12.5L13 17L17.5 12.5" {...SV} /><circle cx="13" cy="5" r="1.8" {...SV} /></svg> }